import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { formatPrice } from 'common/src/utils/functions';
import { metrics__get_sales_per_helper } from 'common/src/api/metrics';


interface HelperSales {
  helper_name: string;
  sales: number;
}

interface HelperSalesData {
  helper_id: number;
  first_name: string | null;
  last_name: string | null;
  total_sales: number;
}

const SalesPerHelperReport: React.FC = () => {
  const [salesData, setSalesData] = useState<HelperSales[]>([]);
  const [startDate, setStartDate] = useState(() => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return thirtyDaysAgo.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(
    () => new Date().toISOString().split('T')[0],
  );
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);

  const fetchSalesData = async (start: string, end: string) => {
    const { data, error } = await metrics__get_sales_per_helper(start, end)

    if (error) {
      console.error('Error fetching sales data:', error);
      return [];
    }

    return (data as HelperSalesData[]).map((item) => ({
      helper_name:
        item.first_name && item.last_name
          ? `${item.first_name} ${item.last_name}`
          : 'Unknown',
      sales: item.total_sales,
    }));
  };

  useEffect(() => {
    fetchSalesData(startDate, endDate).then(setSalesData);
  }, [startDate, endDate]);

  const { salesWithPercentages, totalSales } = useMemo(() => {
    const total = salesData.reduce((sum, helper) => sum + helper.sales, 0);
    const withPercentages = salesData.map((helper) => ({
      ...helper,
      percentage: (helper.sales / total) * 100,
    }));
    withPercentages.sort((a, b) => b.sales - a.sales); // Sort by sales descending
    return { salesWithPercentages: withPercentages, totalSales: total };
  }, [salesData]);

  const handleDateSubmit = () => {
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(salesWithPercentages);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'SalesPerHelper');
    XLSX.writeFile(wb, `sales_per_helper_${startDate}_${endDate}.xlsx`);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between p-4 bg-white border-b shadow-sm">
        <div className="w-96">
          <h3 className="text-lg font-semibold mb-3">
            Seleccionar Rango de Fechas
          </h3>
          <div className="space-y-4">
            <div>
              <label
                htmlFor="startDate"
                className="block text-sm font-medium text-gray-700"
              >
                Fecha de Inicio
              </label>
              <input
                id="startDate"
                type="date"
                value={tempStartDate}
                onChange={(e) => setTempStartDate(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              />
            </div>
            <div>
              <label
                htmlFor="endDate"
                className="block text-sm font-medium text-gray-700"
              >
                Fecha de Fin
              </label>
              <input
                id="endDate"
                type="date"
                value={tempEndDate}
                onChange={(e) => setTempEndDate(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              />
            </div>
            <button
              onClick={handleDateSubmit}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Aplicar Rango
            </button>
          </div>
        </div>

        <div className="w-96">
          <h3 className="text-lg font-semibold mb-3">Otras Opciones</h3>
          <button
            onClick={exportToExcel}
            className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            Exportar a Excel
          </button>
        </div>
      </div>

      <div className="flex-grow overflow-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Helper
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Sales
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Sales %
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {salesWithPercentages.map((helper) => (
              <tr key={helper.helper_name}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {helper.helper_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">
                  {formatPrice(helper.sales)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-500">
                  {helper.percentage.toFixed(2)}%
                </td>
              </tr>
            ))}
            <tr className="bg-gray-50 font-semibold">
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                Total
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                {formatPrice(totalSales)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                100.00%
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SalesPerHelperReport;
